import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import SaveButton from './SaveButton';
import CancelButton from './CancelButton';
import ToggleSwitch from './ToggleSwitch';
import { useErrorToast, useDismissError } from './ErrorToastContext';

// Ticks which of a mailbox's folders Email Sync reads (issue #262,
// vis_email_sync.md Concept 12) — used for a staff member's own mailbox on
// My Account and the shared mailbox on Platform > API Integrations.
// `endpoint` serves GET (the mailbox's folders, listed live from the mail
// server, plus the current choice) and PUT ({ folders: [{ path, subfolders }] }).
// Junk, Trash and Drafts show but can't be ticked. A folder ticked with its
// subfolders covers everything under it, including folders made later, so
// those show as ticked and locked.

const ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 10, minHeight: 32, fontSize: 14 };
const NOTE_STYLE = { fontSize: 12, color: '#888' };

function isUnder(path, parent) {
  return !!parent.delimiter && path.startsWith(`${parent.path}${parent.delimiter}`);
}

function MailboxFolderPicker({ endpoint, onSaved, onCancel }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [folders, setFolders] = useState(null);
  const [choice, setChoice] = useState({});
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(endpoint)
      .then((res) => {
        setFolders(res.data.data.folders);
        const next = {};
        res.data.data.selection.forEach((s) => { next[s.path] = { subfolders: !!s.subfolders }; });
        setChoice(next);
      })
      .catch((err) => setLoadError(err.response?.data?.message || "Couldn't read the mailbox's folders"));
  }, [endpoint]);

  if (loadError) {
    return (
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 13, color: '#b91c1c' }}>{loadError}</div>
        <CancelButton onClick={onCancel} style={{ marginTop: 10 }}>Close</CancelButton>
      </div>
    );
  }
  if (!folders) {
    return (
      <div style={{ marginTop: 14, fontSize: 13, color: '#5a6b85', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Loader2 size={14} strokeWidth={2.5} style={{ animation: 'mailbox-folder-spin 0.8s linear infinite' }} />
        Reading the mailbox&apos;s folders...
        <style>{'@keyframes mailbox-folder-spin { to { transform: rotate(360deg); } }'}</style>
      </div>
    );
  }

  // The ticked folder (with subfolders) this one sits under, if any.
  const coveredBy = (folder) => folders.find((p) => choice[p.path]?.subfolders && isUnder(folder.path, p));

  const toggle = (folder, on) => {
    setChoice((prev) => {
      const next = { ...prev };
      if (on) next[folder.path] = { subfolders: !folder.selectable && folder.has_children };
      else delete next[folder.path];
      return next;
    });
  };

  const setSubfolders = (folder, on) => {
    setChoice((prev) => {
      const next = { ...prev, [folder.path]: { subfolders: on } };
      // Folders under it are now covered, so their own ticks would be noise.
      if (on) folders.forEach((f) => { if (isUnder(f.path, folder)) delete next[f.path]; });
      return next;
    });
  };

  const selection = folders
    .filter((f) => choice[f.path] && !coveredBy(f))
    .map((f) => ({ path: f.path, subfolders: !!choice[f.path].subfolders }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(endpoint, { folders: selection });
      dismissError();
      onSaved(res.data);
    } catch (err) {
      showError(err, 'Could not save the folders');
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 13, color: '#5a6b85', marginBottom: 8 }}>
        Tick the folders to read. A newly ticked folder is read back to the History start date. Unticking one stops it syncing; emails already logged from it stay.
      </div>
      <div style={{ background: '#fff', border: '1px solid #e3e7ee', borderRadius: 6, padding: '6px 10px', maxHeight: 360, overflowY: 'auto' }}>
        {folders.map((folder) => {
          const parent = coveredBy(folder);
          const ticked = !!choice[folder.path] || !!parent;
          const locked = folder.excluded || !!parent;
          let note = null;
          if (folder.excluded) note = 'Never synced';
          else if (parent) note = `With ${parent.name}`;
          else if (!folder.selectable) note = 'Holds only folders';
          return (
            <div key={folder.path} style={{ ...ROW_STYLE, paddingLeft: folder.depth * 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: locked ? 'not-allowed' : 'pointer', color: folder.excluded ? '#999' : '#333' }}>
                <input
                  type="checkbox"
                  checked={ticked && !folder.excluded}
                  disabled={locked}
                  onChange={(e) => toggle(folder, e.target.checked)}
                />
                {folder.name}
                {note && <span style={NOTE_STYLE}>{note}</span>}
              </label>
              {ticked && !parent && !folder.excluded && folder.has_children && (
                <ToggleSwitch
                  checked={!!choice[folder.path]?.subfolders}
                  onChange={(on) => setSubfolders(folder, on)}
                  disabled={!folder.selectable}
                  label="Subfolders"
                  title="Also read every folder under this one, including ones made later"
                />
              )}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12 }}>
        <SaveButton type="button" onClick={handleSave} loading={saving} disabled={!selection.length} />
        <CancelButton onClick={onCancel} disabled={saving} style={{ marginLeft: 8 }} />
      </div>
    </div>
  );
}

export default MailboxFolderPicker;
